import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PlayerSearchSelector from '../../components/PlayerSearchSelector';
import ErrorAlert from '../../components/ErrorAlert';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { apiGet, apiPost } from '../../../services/apiFetch';
import '../../../styles/CreateMatchForm.css';

const SettleMatch = () => {
    const { id, date } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { getAvailablePlayers } = useAdminData();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [team1Info, setTeam1Info] = useState(null);
    const [team2Info, setTeam2Info] = useState(null);
    const [team1Players, setTeam1Players] = useState([]);
    const [team2Players, setTeam2Players] = useState([]);
    const [originalTeam1Players, setOriginalTeam1Players] = useState([]); // Track original players
    const [originalTeam2Players, setOriginalTeam2Players] = useState([]); // Track original players
    const [availablePlayers, setAvailablePlayers] = useState([]); // For player search
    const [playerStats, setPlayerStats] = useState({});
    const [activeGameTab, setActiveGameTab] = useState(1);
    

    const [games, setGames] = useState([
        {
            game_number: 1,
            duration: 300,
            map_name: 'DFH Stadium',
            winner: null
        }
    ]);

    useEffect(() => {
        const fetchMatch = async () => {
            try {
                setLoading(true);
                let currentMatchData;

                if (location.state?.matchData) {
                    console.log("SettleMatch.js: Using passed match data from AdminMatchDetails.js", location.state.matchData);
                    currentMatchData = location.state.matchData;
                } else {
                    const { data: matchData, error } = await apiGet(`/api/rl/matches/upcoming/${id}?date=${date}`);
                    if (error) throw new Error(error.error);
                    currentMatchData = matchData.match;
                }

                const teamsData = currentMatchData.teams;

                const team1 = teamsData.find(t => t.designation === 1);
                const team2 = teamsData.find(t => t.designation === 2);

                // Get IDs of players already on teams
                const existingPlayerIds = [];
                if (team1?.players) {
                    team1.players.forEach(player => existingPlayerIds.push(player.id));
                }
                if (team2?.players) {
                    team2.players.forEach(player => existingPlayerIds.push(player.id));
                }

                // Get available players using context (excluding already assigned players)
                const playersNotOnTeams = getAvailablePlayers(existingPlayerIds);
                setAvailablePlayers(playersNotOnTeams);

                if (team1) {
                    setTeam1Info({
                        id: team1.id,
                        name: team1.name,
                        image: team1.image
                    });
                    setTeam1Players(team1.players || []);
                    setOriginalTeam1Players(team1.players || []);
                }

                if (team2) {
                    setTeam2Info({
                        id: team2.id,
                        name: team2.name,
                        image: team2.image
                    });
                    setTeam2Players(team2.players || []);
                    setOriginalTeam2Players(team2.players || []);
                }

                initializePlayerStats([team1, team2]);

                setLoading(false);
            } catch (error) {
                console.error('Error fetching match:', error);
                setLoading(false);
            } finally {
                setLoading(false);
            }
        };

        fetchMatch();
    }, [id, date]);

    const initializePlayerStats = (teams) => {
        const initialStats = {};

        teams.forEach(team => {
            team.players.forEach(player => {
                const key = `${team.id}-${player.id}`;
                initialStats[key] = {};

                games.forEach(game => {
                    initialStats[key][game.game_number] = {
                        score: null,
                        goals: null,
                        assists: null,
                        saves: null,
                        shots: null,
                        total_distance: null
                    };
                });
            });
        });

        setPlayerStats(initialStats);
    };

    const addGame = () => {
        const newGameNumber = games.length + 1;

        // Add new game
        setGames([
            ...games,
            {
                game_number: newGameNumber,
                duration: 300,
                map_name: 'DFH Stadium',
                blue_team_score: 0,
                orange_team_score: 0,
                winner: null
            }
        ]);

        // Create a deep copy of the current player stats
        const updatedPlayerStats = JSON.parse(JSON.stringify(playerStats));

        // Initialize stats for the new game for all players
        team1Players.forEach(player => {
            const playerId = player.id;
            const key = `${team1Info.id}-${playerId}`;
            // Make sure this player has an entry in the stats object
            if (!updatedPlayerStats[key]) {
                updatedPlayerStats[key] = {};
            }

            // Add stats for the new game number
            updatedPlayerStats[key][newGameNumber] = {
                score: null,
                goals: null,
                assists: null,
                saves: null,
                shots: null,
                total_distance: null
            };
        });

        team2Players.forEach(player => {
            const playerId = player.id;
            const key = `${team2Info.id}-${playerId}`;
            // Make sure this player has an entry in the stats object
            if (!updatedPlayerStats[key]) {
                updatedPlayerStats[key] = {};
            }

            // Add stats for the new game number
            updatedPlayerStats[key][newGameNumber] = {
                score: null,
                goals: null,
                assists: null,
                saves: null,
                shots: null,
                total_distance: null
            };
        });


        // Update the player stats state with the new structure
        setPlayerStats(updatedPlayerStats);
        setActiveGameTab(newGameNumber);
    };

    const removeGame = (gameNumber) => {
        if (games.length <= 1) return;

        const filteredGames = games.filter(game => game.game_number !== gameNumber);

        const renumberedGames = filteredGames.map((game, index) => ({
            ...game,
            game_number: index + 1
        }));

        setGames(renumberedGames);

        const updatedPlayerStats = {};

        Object.keys(playerStats).forEach(key => {
            updatedPlayerStats[key] = {};

            let newGameNumber = 1;

            for (let i = 1; i <= games.length; i++) {
                if (i === gameNumber) continue;

                if (playerStats[key][i]) {
                    updatedPlayerStats[key][newGameNumber] = playerStats[key][i];
                    newGameNumber++;
                }
            }
        });

        setPlayerStats(updatedPlayerStats);

        if (activeGameTab === gameNumber) {
            setActiveGameTab(1);
        } else if (activeGameTab > gameNumber) {
            setActiveGameTab(activeGameTab - 1);
        }
    };

    const handleGameInputChange = (gameNumber, field, value) => {
        let processedValue;
        if (field === 'winner') {
            processedValue = value;
        } else if (field === 'map_name') {
            processedValue = value;
        } else {
            processedValue = value === '' ? 0 : parseInt(value);
        }
        setGames(games.map(game =>
            game.game_number === gameNumber
                ? { ...game, [field]: processedValue }
                : game
        ));
    };

    const handlePlayerStatChange = (teamId, playerId, gameNumber, stat, value) => {
        const key = `${teamId}-${playerId}`;
        let parsedValue = value;
        if (value === '' || value === null || value === undefined) {
            parsedValue = null;
        } else if (typeof value === 'string') {
            parsedValue = parseInt(value);
        }
        setPlayerStats(prevStats => ({
            ...prevStats,
            [key]: {
                ...prevStats[key],
                [gameNumber]: {
                    ...prevStats[key]?.[gameNumber],
                    [stat]: parsedValue
                }
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            // Format game data for the Lambda function
            const gameDetails = {};
            games.forEach(game => {
                // Convert from "team1"/"team2" winner format to 1/2 numerical format
                const winnerValue = game.winner === 'team1' ? 1 :
                    game.winner === 'team2' ? 2 : null;

                // Format team1 player stats for this game
                const team1PlayerStats = {};
                team1Players.forEach(player => {
                    const key = `${team1Info.id}-${player.id}`;
                    const username = player.username;
                    if (playerStats[key] && playerStats[key][game.game_number]) {
                        team1PlayerStats[username] = playerStats[key][game.game_number];
                    }
                });

                // Format team2 player stats for this game
                const team2PlayerStats = {};
                team2Players.forEach(player => {
                    const key = `${team2Info.id}-${player.id}`;
                    const username = player.username;
                    if (playerStats[key] && playerStats[key][game.game_number]) {
                        team2PlayerStats[username] = playerStats[key][game.game_number];
                    }
                });

                // Create game object expected by Lambda
                gameDetails[`game${game.game_number}`] = {
                    duration: game.duration,
                    map: game.map_name,
                    winner: winnerValue,
                    team1score: game.blue_team_score,
                    team2score: game.orange_team_score,
                    team1players: team1PlayerStats,
                    team2players: team2PlayerStats
                };
            });

            // Create submission data in the format expected by Lambda
            const submissionData = {
                matchid: id,
                date,
                gameDetails: gameDetails,
            };

            // Get newly added players for team 1
            const team1AddedPlayers = team1Players.filter(player => {
                const playerId = player.id;
                return !originalTeam1Players.some(original => original.id === playerId);
            });

            // Get newly added players for team 2
            const team2AddedPlayers = team2Players.filter(player => {
                const playerId = player.id;
                return !originalTeam2Players.some(original => original.id === playerId);
            });

            // Add the newly added players to submission data
            submissionData.team1addedplayers = team1AddedPlayers;
            submissionData.team2addedplayers = team2AddedPlayers;

            console.log("settle match submission data", submissionData);

            const { data: result, error: settleError } = await apiPost(`/api/rl/matches/${id}/settle`, submissionData);

            console.log("settle match result", result);

            if (!settleError) {
                navigate('/admin/matches');
            } else {
                setError(settleError);
            }

        } catch (error) {
            console.error('Error settling match:', error);
            setError({
                error: 'internal_error',
                message: error.message || 'Error settling match. Please try again.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Function to add player to team 1
    const addPlayerToTeam1 = (player) => {
        const playerId = player.id;

        if (!team1Players.find(p => p.id === playerId)) {
            const newPlayers = [...team1Players, player];
            setTeam1Players(newPlayers);

            // Initialize stats for the new player
            const key = `${team1Info.id}-${playerId}`;
            setPlayerStats(prevStats => {
                const newStats = { ...prevStats };
                newStats[key] = {};
                games.forEach(game => {
                    newStats[key][game.game_number] = {
                        score: null,
                        goals: null,
                        assists: null,
                        saves: null,
                        shots: null,
                        total_distance: null
                    };
                });
                return newStats;
            });

            // Remove from available players
            setAvailablePlayers(prev => prev.filter(p => p.id !== playerId));
        }
    };

    // Function to add player to team 2
    const addPlayerToTeam2 = (player) => {
        const playerId = player.id;

        if (!team2Players.find(p => p.id === playerId)) {
            const newPlayers = [...team2Players, player];
            setTeam2Players(newPlayers);

            // Initialize stats for the new player
            const key = `${team2Info.id}-${playerId}`;
            setPlayerStats(prevStats => {
                const newStats = { ...prevStats };
                newStats[key] = {};
                games.forEach(game => {
                    newStats[key][game.game_number] = {
                        score: null,
                        goals: null,
                        assists: null,
                        saves: null,
                        shots: null,
                        total_distance: null
                    };
                });
                return newStats;
            });

            // Remove from available players
            setAvailablePlayers(prev => prev.filter(p => p.id !== playerId));
        }
    };

    // Function to remove player from team 1
    const removePlayerFromTeam1 = (playerId) => {
        // Check if this is an original player - if so, don't allow removal
        const isOriginalPlayer = originalTeam1Players.some(p => p.id === playerId);
        if (isOriginalPlayer) {
            return;
        }

        setTeam1Players(prev => prev.filter(p => p.id !== playerId));

        // Remove player stats
        const key = `${team1Info.id}-${playerId}`;
        setPlayerStats(prevStats => {
            const newStats = { ...prevStats };
            delete newStats[key];
            return newStats;
        });

        // Add back to available players
        const removedPlayer = team1Players.find(p => p.id === playerId);
        if (removedPlayer) {
            setAvailablePlayers(prev => [...prev, removedPlayer]);
        }
    };

    // Function to remove player from team 2
    const removePlayerFromTeam2 = (playerId) => {
        // Check if this is an original player - if so, don't allow removal
        const isOriginalPlayer = originalTeam2Players.some(p => p.id === playerId);
        if (isOriginalPlayer) {
            return;
        }

        setTeam2Players(prev => prev.filter(p => p.id !== playerId));

        // Remove player stats
        const key = `${team2Info.id}-${playerId}`;
        setPlayerStats(prevStats => {
            const newStats = { ...prevStats };
            delete newStats[key];
            return newStats;
        });

        // Add back to available players
        const removedPlayer = team2Players.find(p => p.id === playerId);
        if (removedPlayer) {
            setAvailablePlayers(prev => [...prev, removedPlayer]);
        }
    };

    if (loading) {
        return <div className="loading-spinner"></div>;
    }

    return (
        <div className="player-form-page">
            <div className="page-header">
                <h1>Settle Match</h1>
                <h2>{team1Info?.name} vs {team2Info?.name}</h2>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-section">
                    <h2>Game Details</h2>

                    <div className="game-tabs">
                        {games.map(game => (
                            <button
                                key={game.game_number}
                                type="button"
                                className={`game-tab ${activeGameTab === game.game_number ? 'active' : ''}`}
                                onClick={() => setActiveGameTab(game.game_number)}
                            >
                                Game {game.game_number}
                            </button>
                        ))}

                        <button
                            type="button"
                            className="add-game-button"
                            onClick={addGame}
                        >
                            + Add Game
                        </button>
                    </div>

                    {games.map(game => (
                        <div
                            key={game.game_number}
                            className={`game-panel ${activeGameTab === game.game_number ? 'active' : ''}`}
                        >
                            <div className="game-header">
                                <h3>Game {game.game_number}</h3>

                                <button
                                    type="button"
                                    className="remove-game-button"
                                    onClick={() => removeGame(game.game_number)}
                                    disabled={games.length <= 1}
                                >
                                    Remove Game
                                </button>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Map</label>
                                    <select
                                        value={game.map_name}
                                        onChange={(e) => handleGameInputChange(game.game_number, 'map_name', e.target.value)}
                                    >
                                        <option value="DFH Stadium">DFH Stadium</option>
                                        <option value="Mannfield">Mannfield</option>
                                        <option value="Champions Field">Champions Field</option>
                                        <option value="Neo Tokyo">Neo Tokyo</option>
                                        <option value="Wasteland">Wasteland</option>
                                        <option value="Urban Central">Urban Central</option>
                                        <option value="Beckwith Park">Beckwith Park</option>
                                        <option value="Utopia Coliseum">Utopia Coliseum</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Duration (seconds)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={game.duration || ''}
                                        onChange={(e) => handleGameInputChange(game.game_number, 'duration', e.target.value)}
                                        placeholder="0"
                                    />
                                </div>
                            </div>

                            {/*<div className="game-score">
                                <div className="team-score">
                                    <label>{team1Info?.name || 'Team 1'}</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={game.blue_team_score || ''}
                                        onChange={(e) => handleGameInputChange(game.game_number, 'blue_team_score', e.target.value)}
                                        placeholder="0"
                                    />
                                </div>

                                <div className="score-separator">-</div>

                                <div className="team-score">
                                    <label>{team2Info?.name || 'Team 2'}</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={game.orange_team_score || ''}
                                        onChange={(e) => handleGameInputChange(game.game_number, 'orange_team_score', e.target.value)}
                                        placeholder="0"
                                    />
                                </div>
                            </div>*/}

                            <div className="winner-selection">
                                <label>Winner</label>
                                <div className="winner-buttons">
                                    <button
                                        type="button"
                                        className={`winner-button ${game.winner === 'team1' ? 'selected' : ''}`}
                                        onClick={() => handleGameInputChange(game.game_number, 'winner', 'team1')}
                                    >
                                        {team1Info?.name || 'Team 1'}
                                    </button>

                                    <button
                                        type="button"
                                        className={`winner-button ${game.winner === 'team2' ? 'selected' : ''}`}
                                        onClick={() => handleGameInputChange(game.game_number, 'winner', 'team2')}
                                    >
                                        {team2Info?.name || 'Team 2'}
                                    </button>
                                </div>
                            </div>

                            <h4>Player Statistics</h4>

                            <div className="player-stats-section">
                                <div className="team-stats">
                                    <h5>{team1Info?.name || 'Team 1'} Players</h5>

                                    {/* Add Player to Team 1 */}
                                    <div className="add-player-section">
                                        <h6>Add Player to {team1Info?.name || 'Team 1'}</h6>
                                        <PlayerSearchSelector
                                            key={`team1-search-${availablePlayers.length}`}
                                            placeholder="Search for player..."
                                            items={availablePlayers}
                                            onSelect={addPlayerToTeam1}
                                            displayProperty="username"
                                            idProperty="id"
                                            imageProperty="image"
                                        />
                                    </div>

                                    <table className="stats-table">
                                        <thead>
                                            <tr>
                                                <th>Player</th>
                                                <th>Score</th>
                                                <th>Goals</th>
                                                <th>Assists</th>
                                                <th>Saves</th>
                                                <th>Shots</th>
                                                <th>Total Distance</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {team1Players.map(player => (
                                                <tr key={`${team1Info.id}-${player.id}`}>
                                                    <td>{player.username}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team1Info.id}-${player.id}`]?.[game.game_number]?.score ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team1Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'score',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team1Info.id}-${player.id}`]?.[game.game_number]?.goals ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team1Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'goals',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team1Info.id}-${player.id}`]?.[game.game_number]?.assists ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team1Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'assists',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team1Info.id}-${player.id}`]?.[game.game_number]?.saves ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team1Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'saves',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team1Info.id}-${player.id}`]?.[game.game_number]?.shots ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team1Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'shots',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team1Info.id}-${player.id}`]?.[game.game_number]?.total_distance ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team1Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'total_distance',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        {/* Only show remove button for newly added players */}
                                                        {!originalTeam1Players.some(p => p.id === player.id) ? (
                                                            <button
                                                                type="button"
                                                                className="remove-button"
                                                                onClick={() => removePlayerFromTeam1(player.id)}
                                                                title="Remove player from team"
                                                            >
                                                                Remove
                                                            </button>
                                                        ) : (
                                                            <span className="original-player-indicator">Original Player</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="team-stats">
                                    <h5>{team2Info?.name || 'Team 2'} Players</h5>

                                    {/* Add Player to Team 2 */}
                                    <div className="add-player-section">
                                        <h6>Add Player to {team2Info?.name || 'Team 2'}</h6>
                                        <PlayerSearchSelector
                                            key={`team2-search-${availablePlayers.length}`}
                                            placeholder="Search for player..."
                                            items={availablePlayers}
                                            onSelect={addPlayerToTeam2}
                                            displayProperty="username"
                                            idProperty="id"
                                            imageProperty="image"
                                        />
                                    </div>

                                    <table className="stats-table">
                                        <thead>
                                            <tr>
                                                <th>Player</th>
                                                <th>Score</th>
                                                <th>Goals</th>
                                                <th>Assists</th>
                                                <th>Saves</th>
                                                <th>Shots</th>
                                                <th>Total Distance</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {team2Players.map(player => (
                                                <tr key={`${team2Info.id}-${player.id}`}>
                                                    <td>{player.username}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team2Info.id}-${player.id}`]?.[game.game_number]?.score ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team2Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'score',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team2Info.id}-${player.id}`]?.[game.game_number]?.goals ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team2Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'goals',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team2Info.id}-${player.id}`]?.[game.game_number]?.assists ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team2Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'assists',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team2Info.id}-${player.id}`]?.[game.game_number]?.saves ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team2Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'saves',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team2Info.id}-${player.id}`]?.[game.game_number]?.shots ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team2Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'shots',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={playerStats[`${team2Info.id}-${player.id}`]?.[game.game_number]?.total_distance ?? ''}
                                                            onChange={(e) => handlePlayerStatChange(
                                                                team2Info.id,
                                                                player.id,
                                                                game.game_number,
                                                                'total_distance',
                                                                e.target.value
                                                            )}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        {/* Only show remove button for newly added players */}
                                                        {!originalTeam2Players.some(p => p.id === player.id) ? (
                                                            <button
                                                                type="button"
                                                                className="remove-button"
                                                                onClick={() => removePlayerFromTeam2(player.id)}
                                                                title="Remove player from team"
                                                            >
                                                                Remove
                                                            </button>
                                                        ) : (
                                                            <span className="original-player-indicator">Original Player</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <ErrorAlert 
                    error={error} 
                    onDismiss={() => setError(null)}
                    title="Error Settling Match"
                />

                <div className="form-actions">
                    <button
                        type="button"
                        className="cancel-button"
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        className="submit-button"
                        disabled={submitting}
                    >
                        {submitting ? 'Saving...' : 'Settle Match'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SettleMatch; 